<script setup>
import { ref, onMounted, computed } from 'vue';
import {useFormField} from "../../../Registration/composables/useFormField";
import FieldErrorMessage from "../../../Registration/components/Fields/FieldErrorMessage.vue";
import { useI18n } from 'vue-i18n';
import { useLivewireManager } from '../../../Registration/stores/livewireManagerStore';
import { useBookingStore } from '../../../Registration/stores/bookingStore';
import { dataGet } from '../../../Common/utils';

const emit = defineEmits(['blur', 'input']);

const props = defineProps(['item','group','groupId','existingFileName', 'backend']);
const {item, group, groupId, backend} = props;
const {model, uniqueItemId, path, hasError, inputAttributes, isReadOnly, getFieldClasses, createBlurHandler, createInputHandler} = useFormField(item, group, groupId, (v) => v, (v) => v, backend);

const handleBlur = createBlurHandler(emit);
const handleInput = createInputHandler(emit);

let className = getFieldClasses('form-item-input text-field');

const bookingStore = useBookingStore();


const existingFileName = computed(() => {
    return dataGet(bookingStore.data, path, '');
});

const uploading = ref(false);
const livewireManager = useLivewireManager();
const file = ref(null);
const hiddenField = ref(null);



async function handleFileChange(event) {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    file.value = selectedFile;
    uploading.value = true;

    const fileName = selectedFile.name;
    const fileSize = selectedFile.size;
    const fileType = selectedFile.type;

    try {
        bookingStore.startProcessLoader('file-upload-' + uniqueItemId);
        const component = livewireManager.getComponent();
        const urlResponse = await component.call('getTemporaryUploadUrl', fileType, fileSize, item.label);
        if (!urlResponse.ok) {
            uploading.value = false;
            bookingStore.addError(path, urlResponse.message);
            return;
        }
        const uploadResponse = await fetch(urlResponse.data.url, {
            method: 'PUT',
            body: selectedFile,
        });
        const temporaryUploadUuid = await component.call('createTemporaryUpload', urlResponse.data, fileName, fileSize, fileType);
        const fileInputId = event.target.id;
        const hiddenFieldId = fileInputId.replace('file-', '');
        const hiddenField = document.getElementById(hiddenFieldId);
        if (hiddenField) {
            hiddenField.value = temporaryUploadUuid;
            hiddenField.dispatchEvent(new Event('input', { bubbles: true }));
        }
    } catch (error) {
        console.error('Upload error:', error);
    } finally {
        uploading.value = false;
        file.value = null;
        bookingStore.stopProcessLoader('file-upload-' + uniqueItemId);
    }
}

function removeFile(inputId) {
    bookingStore.updateField(path, null);
    const fileInput = document.getElementById('file-' + inputId);
    if (fileInput) fileInput.value = '';
    file.value = null;
    uploading.value = false;
}

const { t } = useI18n();
</script>

<template>
    <div :class="['w-full form-group form-wrapper file-upload-field', className]">
        <label :id="'label-' + uniqueItemId" :for="'file-' + uniqueItemId" class="field-label mr-2">
            {{ item.label }}<span v-if="item.required && !isReadOnly">*</span>
        </label>

        <div v-if="!existingFileName & !uploading">
            <label :for="'file-' + uniqueItemId"
                   class="form-item form-element inline-flex justify-center items-center gap-2 w-full cursor-pointer mb-0!"
                   style="line-height: 18px">
                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
                {{ t('centerstage.ui.registration.file-upload.upload-title') }}
            </label>
            <input type="file"
                   :name="'file-' + uniqueItemId"
                   @change="handleFileChange"
                   :id="'file-' + uniqueItemId"
                   tabindex="0"
                   style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0;"
                   class="file-upload-input"
            >
        </div>

        <div v-else-if="uploading" class="form-item form-element inline-flex justify-center items-center w-full" aria-live="polite">
            <span class="animate-spin mr-2">⏳</span>
            {{ t('ui.registration.file-upload.uploading-title') }}
        </div>

        <div v-else class="form-item form-element flex items-center justify-between">
            <span class="flex-1 truncate pr-2">{{ existingFileName }}</span>
            <button type="button" @click="removeFile" class="text-black w-4 h-4" :aria-label="t('ui.registration.file-upload.remove-file-button')">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        <input :ref="hiddenField" :name="uniqueItemId" :id="uniqueItemId" type="hidden" v-model="model" @blur="handleBlur" @input="handleInput" />
        <FieldErrorMessage :path="path" :backend="backend" />
    </div>
</template>

<style scoped>

.is-readonly input {
    opacity: 0.5;
    pointer-events: none;
    background-color: #f8f9fa;
    cursor: not-allowed;
}

.file-upload-input:focus + label,
.file-upload-input:focus-visible + label {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px #93c5fd;
}

</style>
